import type { Messages } from './en';

/** Simplified Chinese locale. Must match the `en` key set exactly. */
export const zhCN: Messages = {
  'app.title': 'inpaint',

  'theme.toggle': '切换主题',
  'lang.toggle': '切换语言',
  'site.home': '主站',

  'tool.upload': '上传',
  'tool.brush': '画笔',
  'tool.eraser': '橡皮',
  'tool.undo': '撤销',
  'tool.redo': '重做',
  'tool.clear': '清除',
  'tool.erase': '擦除',

  'hero.badge': '🛡️ 100% 本地处理 · 无上传',
  'hero.title': '一键擦除照片中不想要的元素',
  'hero.sub': '涂抹一下，即可移除物体、水印、文字和人物——AI 驱动，完全在你的设备上运行。',
  'hero.cta': '📁 选择图片',
  'hero.hint': '或拖拽 / 粘贴 (Ctrl/⌘+V)',
  'hero.s1': '导入图片',
  'hero.s2': '涂抹要移除的内容',
  'hero.s3': '擦除并下载',
  'import.batchHint': '支持批量导入——批量时所有图片尺寸需一致',
  'import.formats': '支持格式：JPEG · PNG · WebP',

  'feature.privacy.title': '100% 隐私保护',
  'feature.privacy.desc': '图片与 AI 推理全程只在你的浏览器内运行，绝不上传任何数据。',
  'feature.local.title': '完全本地运行',
  'feature.local.desc': 'WASM 本地推理，无需安装、无需服务器，一键即可使用。',
  'feature.hd.title': '高清大图支持',
  'feature.hd.desc': '智能分块处理，百万像素照片也能流畅擦除。',
  'feature.offline.title': '离线可用',
  'feature.offline.desc': '模型本地缓存，断网也能继续擦除。',

  'step.import': '导入图片',
  'step.paint': '涂抹要移除的内容',
  'step.erase': '擦除并下载',

  'batch.eraseAll': '✨ 全部擦除',
  'batch.exportAll': '⬇ 全部下载',
  'batch.eraseAllHint': '将同一掩码应用到每一张图片',
  'batch.sizeError': '图片尺寸必须一致',
  'batch.sameSizeRequired': '批量处理要求所有图片尺寸一致，才能共享同一个固定位置掩码。当前为 {first}×{firstH}，而该图为 {w}×{h}。',
  'batch.count': '{n} 张图片',
  'batch.progress': '图片 {i} / {n}',
  'batch.exportZip': 'inpaint-批量-{n}.zip',
  'batch.exportName': 'inpaint-{i}.{ext}',
  'batch.noResult': '请先执行“全部擦除”以生成结果。',

  'drop.overlay': '松开以导入图片',

  'export.format': '格式',
  'export.quality': '质量',
  'export.download': '下载',
  'export.needImage': '请先加载图片。',

  'brush.size': '大小',
  'brush.maskHint': '红色区域=擦除范围',
  'zoom.fit': '适应窗口',
  'paint.reimport': '选择图片',
  'paint.next': '下一步 →',

  'erase.model': '模型',
  'erase.back': '继续涂抹',
  'erase.manage': '模型管理',

  'result.viewOriginal': '👁 按住查看原图',
  'result.zip': '🗜 下载 ZIP',
  'result.edit': '✏️ 继续编辑',
  'result.choose': '📁 选择图片',

  'view.toggleOriginal': '显示原图（点击切换）',

  'model.add': '添加模型',
  'model.change': '⇄ 切换模型',
  'model.storage': '存储',
  'model.add.confirm': '添加',
  'model.url.label': '模型 URL (.onnx)',
  'model.name.label': '名称（可选）',
  'model.upload.pick': '选择 .onnx 文件',
  'model.upload.file': '已选择：{name}',
  'model.req.intro': '模型必须符合 LaMa 约定：',
  'model.req.single': '单个 .onnx 文件（不支持 .onnx.data 外部权重拆分）。',
  'model.req.input': '输入 [1,4,H,W]：通道 0–2 为被遮罩 RGB，通道 3 为二值掩码；H/W 必须是 32 的倍数。',
  'model.req.output': '输出 [1,3,H,W]：修复后的 RGB。',
  'model.req.size': '建议输入 ≤ 512×512，文件 ≤ 512MB。',
  'model.add.empty': '请输入模型 URL 或选择 .onnx 文件。',
  'model.add.badUrl': 'URL 必须指向 .onnx 文件。',
  'model.add.smokeFail': '该模型不兼容（冒烟测试失败）：{reason}',
  'model.delete.confirm': '删除模型“{name}”？删除后需重新下载才能使用。',

  'model.lite.name': '轻量',
  'model.lite.note': 'int8，快速，低内存',
  'model.balanced.name': '均衡',
  'model.balanced.note': 'fp16，质量与速度均衡',
  'model.quality.name': '高质量',
  'model.quality.note': 'fp32，质量最佳',

  'model.download.title': '下载模型',
  'model.download.prompt': '将从 {source} 下载 {name} 模型（{size} MB）。请确保网络可用。',
  'model.download.offline': '网络不可用 — 无法下载模型（已缓存的模型仍可离线使用）。',
  'model.download.busy': '已有模型正在下载，请等待其完成后再试。',
  'model.download.progress': '正在下载 {name}… {loaded} / {total} MB（{pct}%）',
  'model.download.fail.offline': '网络不可用。',
  'model.download.fail.quota': '存储空间不足，无法保存该模型。',
  'model.download.fail.network': '无法访问模型源。',
  'model.download.fail.cache': '无法将模型存入浏览器缓存。',
  'model.download.cancelled': '下载已取消。',

  'model.use': '使用',
  'model.download': '下载',
  'model.delete': '删除',
  'model.redownload': '重新下载',
  'model.cached': '已缓存',
  'model.needDownload': '需要下载',
  'model.needDownloadConfirm': '该模型尚未下载{size}，擦除前需要先下载。仍要选择它吗？',
  'model.recommended': '推荐',
  'model.notRecommended': '不推荐',
  'model.why.noGpu': '没有 WebGPU 时较慢。',
  'model.why.noFp16': '此设备缺少 WebGPU 的 shader-f16 特性，fp16 模型无法在 GPU 上运行。请改用 fp32 的 quality 模型。',
  'model.why.int8': '不支持 fp16 的设备上，轻量版（int8）最快。',
  'model.why.memory': '低内存设备建议使用轻量版。',
  'model.why.mobile': '移动端建议。',
  'model.why.threads': '缺少跨源隔离，只能单线程。',
  'model.why.storage': '可用存储不足（需要 {needed} MB）。',
  'model.why.offline': '离线且未缓存。',

  'inference.title': '正在擦除…',
  'inference.cancel': '取消',
  'inference.initializing': '正在加载模型…（首次使用可能需要一些时间）',
  'inference.initFetching': '正在获取模型…',
  'inference.parsing': '正在解析模型…',
  'inference.elapsed': '{stage}（已耗时 {s} 秒）',
  'inference.tileAvg': '约 {s} 秒/块',
  'inference.erasingElapsed': '正在擦除… 已耗时 {s} 秒',
  'inference.initTimeout': '模型加载超时。模型可能对当前设备过大——请尝试使用轻量版模型。',
  'inference.error': '擦除失败：{reason}',
  'inference.emptyMask': '请先涂抹要移除的对象。',
  'inference.noModel': '未选择模型。',
  'inference.cancelled': '擦除已取消。',
  'inference.running': '擦除正在进行，请等待完成后再导入新图片。',
  'inference.smokeTest': '正在验证模型兼容性…',

  'storage.text': '存储使用：已用 {used} MB / 配额 {quota} MB（{pct}%）。已缓存模型：{models}。',

  'dlg.cancel': '取消',
  'dlg.ok': '确定',
  'dlg.confirm': '确认',
  'dlg.done': '完成',
  'dlg.export.title': '下载图片',
  'dlg.model.title': '模型管理',

  'error.unsupported': '您的浏览器不支持该图片格式。',
  'error.tooLarge': '图片过大：{w}×{h} 超过 {limit}px 边长上限。建议改用更小的图片。',
  'error.read': '无法读取该图片文件。',
  'error.unsupportedFormat': '不支持的图片格式',
  'error.unsupportedFormatDetail': '以下文件因格式不支持已跳过：{names}',
  'error.resource': '设备资源不足',
  'error.resourceDetail': '当前图片或模型占用的内存超出设备可用资源。请尝试使用更小的图片或轻量模型；如果是在 iPhone 或 iPad 上处理大图，建议改用电脑端操作。',

  'faq.title': '常见问题',
  'faq.q1': '收费吗？',
  'faq.a1': '不收费，完全开源。无需注册，无需付费，无任何限制。',
  'faq.q2': '会泄露我的隐私吗？',
  'faq.a2': '不会。照片和 AI 处理全程在你的浏览器本地完成，不会上传到任何服务器。',
  'faq.q3': '推荐什么设备？',
  'faq.a3': '已做优化，推荐使用电脑端 Chrome 浏览器以获得最佳速度；其他现代浏览器也可以正常使用。',
  'faq.q4': '如何删除已下载的模型？',
  'faq.a4': '在编辑页点击「⇄ 切换模型」，找到已下载的模型，点击「删除」即可释放存储空间。',
  'faq.q5': '可以用自己的模型吗？',
  'faq.a5': '可以，只要符合 LaMa 规范即可。在编辑页点击「⇄ 切换模型」，通过设置模型 URL 下载，或直接上传本地 .onnx 文件。',
  'faq.q6': '需要联网吗？',
  'faq.a6': '仅首次需要：首次执行会下载 LaMa 模型，之后模型缓存在本地，无需再联网即可继续使用。',
  'faq.q7': '模型从哪里来？',
  'faq.a7': '内置模型来自 Hugging Face 社区仓库 g-ronimo/lama（LaMa 的 ONNX 社区打包，每个约 62–209 MB），按需下载并缓存在本地。你也可以在「⇄ 切换模型」中添加自己的模型 URL 或上传本地 .onnx 文件。',

  'footer.openSource': '开源 · MIT 协议',
  'footer.privacy': '隐私政策',
  'footer.terms': '服务条款',
  'footer.pricing': '价格',
  'footer.contact': '联系我们',
  'footer.products': '更多产品',

  'github.link': '在 GitHub 查看源码',
};
